import { useEffect, useRef, useState } from 'react';
import { getValueFromNestedObject } from '../../../util/operations/helper';
import InputToggle from './input-toggle';
import SearchDropdown from './search-dropdown';
import TextBox from './text-box';
import { checkIfObjectContainsKeys } from '../../../util/helper';
import { ChipList } from './chip-list';
import { PlusIcon, MinusIcon } from '@heroicons/react/16/solid';

const VALIDATION_TYPES = {
    REQUIRED: 'REQUIRED',
    UNIQUE: 'UNIQUE'
};

/**
 * @typedef {Object} Fields
 * @prop {string} name
 * @prop {string} label
 * @prop {string} type
 */

/**
 *
 * @param {Object} props
 * @param {Fields[]} props.fields
 * @param {import('formik').FormikProps} props.formik
 * @param {string} props.name
 * @param {string} props.className
 * @param {boolean} props.required
 * @param {string} props.label
 * @param {Object} props.attributes
 * @param {boolean} props.disabled
 * @param {boolean} props.readOnly
 */
export default function InputGroup({
    fields = [],
    formik,
    name,
    className,
    required,
    label,
    attributes = {},
    disabled,
    readOnly,
    ...props
}) {
    const { requiredFields = null, uniqueFields = null } = props;
    const [schema, setSchema] = useState(null);
    // Clean up registered fields
    const registeredFieldsRef = useRef({});
    // Contains the value of the input group
    const inputGroupValue = getValueFromNestedObject(formik.values, name, {
        strict: false,
    }) || [];

    // ----- Helper starts -----
    const isFieldsValidatedRef = useRef(false);
    useEffect(() => {
        const requiredProps = [
            'formik',
            'name',
            'label',
            'required',
            'disabled',
            'readOnly',
            'type',
        ];
        if (fields && isFieldsValidatedRef.current) {
            // Validate if each fields
            for (const eachField of fields)
                checkIfObjectContainsKeys(
                    { ...eachField, formik, label, name, disabled, readOnly },
                    requiredProps
                );
            isFieldsValidatedRef.current = false;
        }
    }, [fields, formik, label, name, disabled, readOnly]);

    function formatFieldForValidation(fields, validationType, result = {}) {
        if (typeof fields === 'string') {
            result[fields] = [validationType];
        } else if (Array.isArray(fields)) {
            fields.reduce((acc, curr) => {
                if (acc[curr]) acc[curr].push(validationType);
                else acc[curr] = [validationType];
                return acc;
            }, result);
        }
        return result;
    }
    // ----- Helper stops -----


    useEffect(() => {
        if (!schema) {
            const fieldStructure = {};
            fields.forEach((_field) => {
                fieldStructure[_field?.key] = '';
            });
            setSchema(fieldStructure);
        }
    }, [fields, schema]);

    // Validation 
    useEffect(() => {
        const validation_fields = {};
        if (!!requiredFields) formatFieldForValidation(requiredFields, VALIDATION_TYPES.REQUIRED, validation_fields);
        if (!!uniqueFields) formatFieldForValidation(uniqueFields, VALIDATION_TYPES.UNIQUE, validation_fields);

        if (inputGroupValue?.length && Object.keys(validation_fields).length) {
            for (let i = 0; i < Object.keys(validation_fields).length; i++) {
                const field_key = Object.keys(validation_fields)[i];
                const field_label = fields.find((field) => field.key === field_key)?.label || field_key;
                // add validation to all the fields based on the input group value
                inputGroupValue.forEach((_, index) => {
                    const fieldPath = [name, index, field_key].join(".");
                    // Add the registered fields for cleanup
                    const existing = registeredFieldsRef.current[index] || [];
                    if (!existing.includes(fieldPath)) {
                        registeredFieldsRef.current[index] = [...existing, fieldPath];
                    }
                    formik.registerField(fieldPath, {
                        validate: (value) => {
                            // Required validation
                            if (validation_fields[field_key].includes(VALIDATION_TYPES.REQUIRED)) {
                                if (!value || (typeof value === 'string' && !value.trim())) {
                                    return `${field_label} is required.`;
                                }
                            }
                            // Duplicate validation
                            if (validation_fields[field_key].includes(VALIDATION_TYPES.UNIQUE)) {
                                const fieldValues = inputGroupValue.map(field => field[field_key]);
                                const duplicates = fieldValues.filter(val => val === value);
                                if (duplicates.length > 1) {
                                    return `${field_label} must be unique`;
                                }
                            }
                            // Add other field validation here
                            return undefined;
                        },
                    });
                    // In case duplicates show that fields are touched
                    if (validation_fields[field_key].includes(VALIDATION_TYPES.UNIQUE)) {
                        formik.setFieldTouched(fieldPath, true);
                    }
                });
            }
            // Clean Up
            return () => {
                for (let i = 0; i < Object.keys(validation_fields).length; i++) {
                    const field_key = Object.keys(validation_fields)[i];
                    // Loop through all the fields and add the formik field to be not empty
                    inputGroupValue.forEach((_, index) => {
                        const fieldPath = [name, index, field_key].join(".");
                        formik.unregisterField(fieldPath);

                    });
                }
            };

        }
    }, [uniqueFields, requiredFields, name, inputGroupValue]);

    // Clean up removed fields
    useEffect(() => {
        // Indices of fields that are registered but currently not available
        const removedFieldsIndex = Object.keys(registeredFieldsRef.current).filter(index => index >= inputGroupValue.length);
        for (const index of removedFieldsIndex) {
            // Field paths that are registered
            for (const path of registeredFieldsRef.current[index]) {
                formik.unregisterField(path);
            }
            delete registeredFieldsRef.current[index];
        }
    }, [inputGroupValue]);


    /**
     *
     * @param {number} index
     * @param {"ADD" | "DELETE"} ops
     */
    function modifyField(index, ops) {
        const currentValue = getValueFromNestedObject(formik.values, name, {
            strict: false,
        }) || [];

        if (ops === 'ADD') {
            if (Array.isArray(currentValue)) {
                let newValue;
                if (currentValue.length) {
                    newValue = [...currentValue];
                    newValue.splice(index, 0, { ...schema });
                } else {
                    newValue = [{ ...schema }];
                }
                formik.setFieldValue(name, newValue);
            }
        } else if (ops === 'DELETE') {
            if (Array.isArray(currentValue)) {
                const newValue = [...currentValue];
                newValue.splice(index, 1);
                formik.setFieldValue(name, newValue);
            }
        }
    }

    return (
        <div
            id={label.toLowerCase().replace(' ', '')}
            className={`container mx-auto m-4 col-span-3 border rounded`}
        >
            <div className='flex flex-col justify-start items-start w-full'>
                <div className='ml-3 px-2 -my-2.5 bg-white'>
                    <h4 className='text-sm text-gray-700'>{label}</h4>
                </div>
                <div className='mt-4 mb-4 rounded px-6 py-2'>
                    <div
                        className={`flex flex-row w-full items-center justify-start flex-wrap gap-4 ${className}`}
                        {...attributes}
                    >
                        {inputGroupValue && inputGroupValue?.length ? (
                            inputGroupValue.map((_, index) => {
                                return (
                                    <GroupedInput
                                        key={name + index}
                                        fieldIndex={index}
                                        groupedFields={fields}
                                        formik={formik}
                                        readOnly={readOnly}
                                        disabled={disabled}
                                        parentName={name}
                                        addField={modifyField.bind(this, index + 1, 'ADD')}
                                        removeField={modifyField.bind(this, index, 'DELETE')}
                                    />
                                );
                            })
                        ) : (
                            <div className='flex flex-row items-center justify-start gap-3 pt-1'>
                                <p className='text-gray-500 text-sm'>
                                    {readOnly ? 'Empty' : `Add`}
                                </p>
                                {
                                    !readOnly &&
                                    <button
                                        className='rounded-full bg-green-200 hover:bg-green-600 w-5 p-0.5 text-green-900 hover:text-white'
                                        onClick={modifyField.bind(this, 0, 'ADD')}
                                    >
                                        <PlusIcon />
                                    </button>
                                }

                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function InputField({
    formik,
    readOnly,
    label,
    name,
    disabled,
    type,
    ...props
}) {
    switch (type) {
        case 'toggle':
            return (
                <InputToggle
                    {...props}
                    formik={formik}
                    readOnly={readOnly}
                    label={label}
                    name={name}
                    disabled={disabled}
                />
            );
        case 'chip-list':
            return (
                <ChipList
                    {...props}
                    formik={formik}
                    readOnly={readOnly}
                    label={label}
                    name={name}
                    disabled={disabled}
                />
            );
        case 'dropdown':
            return (
                <SearchDropdown
                    {...props}
                    formik={formik}
                    readOnly={readOnly}
                    label={label}
                    name={name}
                    disabled={disabled}
                    showNull={!props.required}
                />
            );
        default:
            return (
                <TextBox
                    {...props}
                    formik={formik}
                    readOnly={readOnly}
                    label={label}
                    name={name}
                    disabled={disabled}
                    type={type}
                />
            );
    }
}

function GroupedInput({
    fieldIndex,
    groupedFields,
    formik,
    readOnly,
    disabled,
    parentName,
    addField,
    removeField,
}) {
    return (
        <div className='flex flex-row w-full items-start justify-start gap-6'>
            {groupedFields.map((props, index) => {
                return (
                    <InputField
                        {...props}
                        key={[fieldIndex, props.key, index].join('.')}
                        formik={formik}
                        readOnly={readOnly}
                        label={props.label}
                        name={[parentName, fieldIndex, props.key].join('.')}
                        disabled={disabled}
                        type={props.type}
                        required={props.required}
                    />
                );
            })}
            {!readOnly && (
                <div className='flex flex-row self-start justify-center items-start gap-4 pt-9'>
                    <button
                        className='rounded-full bg-green-200 hover:bg-green-600 w-5 p-0.5 text-green-900 hover:text-white'
                        onClick={addField}
                    >
                        <PlusIcon />
                    </button>

                    <button
                        className='rounded-full bg-red-200 hover:bg-red-600 w-5 p-0.5 text-red-900 hover:text-white'
                        onClick={removeField}
                    >
                        <MinusIcon />
                    </button>
                </div>
            )}
        </div>
    );
}
